import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Check, CheckCheck, FileText, ExternalLink, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import SignaturePreviewModal from "@/components/SignaturePreviewModal";

/**
 * Notification bell — polls `/api/notifications/inbox` every 60s and shows
 * a red badge with the unread count. Opens a Popover listing the last 10
 * notifications. Currently the only source is `contract_signed` events
 * (fired when a customer signs online), but any future in-app alert can
 * plug into the same collection.
 */
export function NotificationBell({ collapsed = false }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { kind, recordId }
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/inbox?limit=15");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* silent — bell is not critical */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const markAllRead = async () => {
    setBusy(true);
    try {
      await api.post("/notifications/read-all");
      await load();
      toast.success("Tüm bildirimler okundu");
    } catch { toast.error("Güncellenemedi"); }
    finally { setBusy(false); }
  };

  const openItem = async (n) => {
    // Mark as read first (background), then react based on type
    try { await api.post(`/notifications/${n.id}/read`); } catch { /* ok */ }
    load();
    if (n.type === "contract_signed" && n.contract_id) {
      nav(`/contracts/${n.contract_id}/edit`);
      return;
    }
    if (n.type === "service_signed" && n.service_kind && n.record_id) {
      // Pop-up ile canlı imza önizleme + PDF indir
      setPreview({ kind: n.service_kind, recordId: n.record_id });
      return;
    }
  };

  const fmtRel = (iso) => {
    if (!iso) return "";
    const diffMs = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diffMs / 60_000);
    if (m < 1) return "az önce";
    if (m < 60) return `${m}dk önce`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}sa önce`;
    return `${Math.floor(h / 24)}g önce`;
  };

  return (
    <>
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm"
                title="Bildirimler"
                className="flex-1 apple-ease relative"
                data-testid="notif-bell-btn">
          <Bell className="w-4 h-4" />
          {!collapsed && <span className="ml-2 text-xs">Bildirim</span>}
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
                  data-testid="notif-unread-badge">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent side="right" align="end" className="w-96 p-0"
                       data-testid="notif-popover">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <div className="text-sm font-semibold flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Bildirimler
            {unread > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-800">
                {unread} okunmadı
              </span>
            )}
          </div>
          {unread > 0 && (
            <Button size="sm" variant="ghost" onClick={markAllRead}
                    disabled={busy}
                    className="h-7 text-xs" data-testid="notif-readall">
              <CheckCheck className="w-3.5 h-3.5 mr-1" />
              Tümünü Okundu İşaretle
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-auto">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center p-6">
              Bildirim yok.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {items.map(n => (
                <li key={n.id}
                    className={`p-3 hover:bg-slate-50 cursor-pointer transition ${!n.read ? "bg-blue-50/40" : ""}`}
                    onClick={() => openItem(n)}
                    data-testid={`notif-item-${n.id}`}>
                  <div className="flex items-start gap-2">
                    {n.type === "contract_signed" ? (
                      <FileText className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                    ) : n.type === "service_signed" ? (
                      <PenLine className="w-4 h-4 text-sky-600 mt-0.5 shrink-0" />
                    ) : (
                      <Bell className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {n.title || "Bildirim"}
                        {!n.read && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-blue-500 align-middle"></span>}
                      </div>
                      <div className="text-xs text-muted-foreground whitespace-pre-line line-clamp-3">
                        {n.body}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-2">
                        <span>{fmtRel(n.created_at)}</span>
                        {n.wa_link && (
                          <a href={n.wa_link} target="_blank" rel="noopener noreferrer"
                             onClick={e => e.stopPropagation()}
                             className="text-emerald-600 hover:underline flex items-center gap-0.5"
                             data-testid={`notif-wa-${n.id}`}>
                            WhatsApp <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        )}
                      </div>
                    </div>
                    {!n.read && (
                      <button onClick={(e) => {
                        e.stopPropagation();
                        api.post(`/notifications/${n.id}/read`).then(load);
                      }} title="Okundu işaretle"
                              className="p-1 rounded hover:bg-slate-200"
                              data-testid={`notif-read-${n.id}`}>
                        <Check className="w-3 h-3 text-slate-500" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
    <SignaturePreviewModal
      open={!!preview}
      onClose={() => setPreview(null)}
      kind={preview?.kind}
      recordId={preview?.recordId}
    />
    </>
  );
}
